import { CircleLoader } from '@components/ui';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/Table/Table';
import { ColumnDef, ColumnSizingState, Header, Row, Table as TanstackTable, flexRender } from '@tanstack/react-table';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CellPopoverState, CellValuePopover } from '../CellValuePopover/CellValuePopover';
import { TableCellValue } from '../TableCellValue/TableCellValue';

import styles from './DataTableRenderer.module.scss';
import './DataTableRenderer.types';
import { useFreezeColumnWidths } from './useFreezeColumnWidths';

const humanizeColumnId = (columnId: string) =>
  columnId
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, char => char.toUpperCase());

// Called as a function rather than via flexRender: columns are rebuilt whenever filter counts refetch,
// and flexRender would mount each new header function as a new component type, resetting open popovers.
const renderHeader = <TData, TValue>(header: Header<TData, TValue>) => {
  const headerDef = header.column.columnDef.header;
  return typeof headerDef === 'function' ? headerDef(header.getContext()) : headerDef;
};

interface DataTableRendererProps<TData, TValue> {
  table: TanstackTable<TData>;
  columns: ColumnDef<TData, TValue>[];
  /** id applied to the outer <Table> element (preserves e2e hooks like MODEL_TABLE_ID). */
  tableId?: string;
  /** id applied to <TableBody>. */
  bodyId?: string;
  /** id applied to every data <TableRow>. */
  rowId?: string;
  /** Shows a CircleLoader instead of rows/empty-state. */
  loader?: boolean;
  /** Empty-state content shown when there are no rows and not loading. */
  emptyMessage?: React.ReactNode;
  tableClassName?: string;
  rowClassName?: (row: TData) => string | undefined;
  onRowClick?: (row: Row<TData>) => void;
  /** Rendered below the table inside the card (e.g. pagination). */
  footer?: React.ReactNode;
  /** Wraps the table in a card. Pass false to render the bare table. */
  card?: boolean;
}

export const DataTableRenderer = <TData, TValue>({
  table,
  columns,
  tableId,
  bodyId,
  rowId,
  loader = false,
  emptyMessage = 'No results.',
  tableClassName,
  rowClassName,
  onRowClick,
  footer,
  card = true,
}: DataTableRendererProps<TData, TValue>) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [popoverState, setPopoverState] = useState<CellPopoverState | null>(null);
  const [isFrozen, setIsFrozen] = useState(false);

  const closePopover = useCallback(() => setPopoverState(null), []);

  const sizingState = JSON.stringify(table.getState().columnSizing);
  const sortingState = JSON.stringify(table.getState().sorting);
  const paginationState = JSON.stringify(table.getState().pagination);
  // Popover must close on page change / sort / resize — a pinned popover pointing at a
  // cell that's no longer visible or in the same position is stale.
  useEffect(() => {
    closePopover();
  }, [sortingState, paginationState, sizingState, closePopover]);

  const handleFreeze = useCallback(
    (sizing: ColumnSizingState) => {
      table.setColumnSizing(sizing);
      setIsFrozen(true);
    },
    [table],
  );

  const { rows } = table.getRowModel();
  useFreezeColumnWidths(tableRef, table, handleFreeze, rows.length);

  // when first column select (radio button), then pin the second column all together
  const visibleColumns = table.getVisibleLeafColumns();
  const pinnedColumns = visibleColumns.slice(0, visibleColumns[0]?.id === 'select' ? 2 : 1);
  const pinnedLeft = new Map<string, number>();
  pinnedColumns.reduce((offset, column) => {
    pinnedLeft.set(column.id, offset);
    return offset + column.getSize();
  }, 0);
  const pinnedEdgeId = pinnedColumns[pinnedColumns.length - 1]?.id;
  const getPinnedProps = (columnId: string) =>
    pinnedLeft.has(columnId)
      ? {
          className: clsx(styles.pinnedCell, columnId === pinnedEdgeId && styles.pinnedEdge),
          style: { left: pinnedLeft.get(columnId) },
        }
      : {};

  useEffect(() => {
    const scroller = tableRef.current?.parentElement;
    if (!scroller) return undefined;
    const sync = () => scroller.toggleAttribute('data-scrolled', scroller.scrollLeft > 0);
    sync();
    scroller.addEventListener('scroll', sync, { passive: true });
    return () => scroller.removeEventListener('scroll', sync);
  }, []);

  const tableElement = (
    <>
      <Table
        ref={tableRef}
        id={tableId}
        tableClassName={clsx(styles.scroller, tableClassName)}
        className={isFrozen ? styles.fixedLayout : undefined}
      >
        {isFrozen && (
          <colgroup>
            {table.getVisibleLeafColumns().map(column => (
              <col
                key={column.id}
                style={{ width: column.getSize() }}
              />
            ))}
          </colgroup>
        )}
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const { meta } = header.column.columnDef;
                const canResize = header.column.getCanResize() && !meta?.fixed && header.column.id !== 'select';
                return (
                  <TableHead
                    key={header.id}
                    data-column-id={header.column.id}
                    {...getPinnedProps(header.column.id)}
                    className={clsx(
                      canResize && styles.resizableHeaderCell,
                      getPinnedProps(header.column.id).className,
                    )}
                  >
                    {header.isPlaceholder ? null : renderHeader(header)}
                    {canResize && (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={clsx(
                          styles.resizeHandle,
                          header.column.getIsResizing() && styles.resizeHandleActive,
                        )}
                      />
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody id={bodyId}>
          {loader && (
            <TableRow className="border-0 hover:bg-transparent">
              <TableCell colSpan={columns.length}>
                <CircleLoader className={styles.loader} />
              </TableCell>
            </TableRow>
          )}
          {!loader && rows.length
            ? rows.map(row => (
                <TableRow
                  id={rowId}
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={rowClassName?.(row.original)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {row.getVisibleCells().map(cell => {
                    const { meta } = cell.column.columnDef;
                    const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());
                    return (
                      <TableCell
                        key={cell.id}
                        data-column-id={cell.column.id}
                        {...getPinnedProps(cell.column.id)}
                      >
                        {meta?.clamp ? (
                          <TableCellValue
                            content={rendered}
                            onExpand={(referenceElement, content) =>
                              setPopoverState({
                                referenceElement,
                                title: meta.title ?? humanizeColumnId(cell.column.id),
                                content,
                              })
                            }
                          />
                        ) : (
                          rendered
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            : !loader && (
                <TableRow className="border-0 hover:bg-transparent">
                  <TableCell
                    colSpan={columns.length}
                    className="h-32 text-center"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
        </TableBody>
      </Table>
      {footer && <div className={styles.cardFooter}>{footer}</div>}
    </>
  );

  return (
    <>
      {card ? <div className={styles.card}>{tableElement}</div> : tableElement}
      <CellValuePopover
        state={popoverState}
        onClose={closePopover}
      />
    </>
  );
};
